import { generateCodeChallengeS256, generateCodeVerifier } from "./pkce";

/**
 * Resolve tenant + client id from build-time env or backend (Docker-friendly).
 */
export async function fetchMicrosoftOAuthIds() {
  let tenantId = process.env.REACT_APP_MICROSOFT_TENANT_ID?.trim();
  let clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID?.trim();
  if (tenantId && clientId) {
    return { tenantId, clientId };
  }

  const base = window.location.origin;
  const res = await fetch(`${base}/api/auth/sso-metadata`, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  if (!res.ok) {
    throw new Error(`SSO metadata HTTP ${res.status}`);
  }

  let data;
  try {
    data = await res.json();
  } catch (e) {
    throw new Error("SSO metadata is not JSON");
  }

  tenantId = data.microsoftTenantId != null ? String(data.microsoftTenantId).trim() : "";
  clientId = data.microsoftClientId != null ? String(data.microsoftClientId).trim() : "";

  return { tenantId, clientId };
}

/**
 * Start Microsoft authorization-code + PKCE flow (same host redirect URI).
 */
function newOAuthState() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

export async function startMicrosoftPkceRedirect(options = {}) {
  const { tenantId, clientId } = await fetchMicrosoftOAuthIds();
  if (!tenantId || !clientId) {
    throw new Error("Microsoft SSO is not configured on the server (MICROSOFT_TENANT_ID / MICROSOFT_CLIENT_ID).");
  }

  const redirectUri = `${window.location.origin}/auth/callback`;
  const state = newOAuthState();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallengeS256(codeVerifier);
  const loginHint = (options.loginHint || "").trim();

  // localStorage survives the Microsoft round-trip more reliably than sessionStorage in some browsers/setups.
  localStorage.setItem(`oauth_pkce_v1_${state}`, codeVerifier);
  sessionStorage.setItem("msal_pkce_verifier", codeVerifier);
  sessionStorage.setItem("msal_redirect_uri", redirectUri);
  sessionStorage.setItem("msal_oauth_state", state);
  // Fallback for browsers that block storage (e.g. Edge Tracking Prevention / strict policies).
  // Cookie survives the redirect and is often allowed even when storage is blocked.
  try {
    document.cookie = `oauth_pkce_v1_${encodeURIComponent(state)}=${encodeURIComponent(codeVerifier)}; Max-Age=600; Path=/; SameSite=Lax`;
  } catch (e) {
    // ignore
  }

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    redirect_uri: redirectUri,
    response_mode: "query",
    scope: "openid profile email User.Read",
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  if (loginHint) {
    params.set("login_hint", loginHint);
  }
  // After logout, Microsoft still has the previous browser session. Without this,
  // Sign in silently re-opens that same user. Cross-app login_hint keeps silent SSO.
  const prompt = (options.prompt || "").trim() || (loginHint ? "" : "select_account");
  if (prompt) {
    params.set("prompt", prompt);
  }
  if (!loginHint) {
    params.set("max_age", "0");
  }

  window.location.href = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
}
